import {
  BalanceThresholds,
  FiatCurrency,
  PaymentWalletConfig,
  paymentOperationsApi as api,
} from "api/admin/paymentOperations";
import AdminSidebar from "components/layout/AdminSidebar";
import Container from "components/layout/Container";
import Main from "components/layout/Main";
import useAdminAccess from "hooks/useAdminAccess";
import { currentAdminCountryScope } from "helpers/storage";
import { ReactNode, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { showDanger, showSuccess } from "utilities/misc";
import {
  Button,
  Card,
  ErrorState,
  LoadingRows,
  errorMessage,
  fieldClass,
} from "../access-control/components";
const currencies = ["NGN", "GHS", "KES"] as const;
function SettingsForm({
  title,
  children,
  save,
  editable,
}: {
  title: string;
  children: ReactNode;
  save: () => Promise<unknown>;
  editable: boolean;
}) {
  const client = useQueryClient();
  const [busy, setBusy] = useState(false);
  return (
    <Card title={title}>
      <form
        className="p-5 space-y-4"
        onSubmit={async (event) => {
          event.preventDefault();
          if (!editable || busy) return;
          setBusy(true);
          try {
            await save();
            showSuccess(`${title} saved`);
            await client.invalidateQueries("payment-operations");
          } catch (error) {
            showDanger(errorMessage(error));
          } finally {
            setBusy(false);
          }
        }}
      >
        <fieldset disabled={!editable || busy} className="space-y-4">
          {children}
          {editable && (
            <Button type="submit" disabled={busy}>
              {busy ? "Saving…" : "Save changes"}
            </Button>
          )}
        </fieldset>
      </form>
    </Card>
  );
}
function Wallets({
  config,
  editable,
}: {
  config: PaymentWalletConfig;
  editable: boolean;
}) {
  const [provider, setProvider] = useState(
    config.providers.find((p) => p.key !== "none")?.key || "",
  );
  const [currency, setCurrency] = useState<FiatCurrency | "">(
    config.activeWalletByProvider[provider] || "",
  );
  return (
    <SettingsForm
      title="Provider wallet"
      editable={editable}
      save={() => api.saveWallet(provider, currency as FiatCurrency)}
    >
      <p className="text-sm text-gray-500">
        Choose the settlement wallet currency used by a provider. Payment
        routing is managed in Payment Merchant.
      </p>
      <label className="block">
        Provider
        <select
          required
          className={fieldClass}
          value={provider}
          onChange={(e) => {
            setProvider(e.target.value);
            setCurrency(config.activeWalletByProvider[e.target.value] || "");
          }}
        >
          {config.providers
            .filter((p) => p.key !== "none")
            .map((p) => (
              <option key={p.key} value={p.key}>
                {p.name || p.title || p.key}
              </option>
            ))}
        </select>
      </label>
      <label className="block">
        Wallet currency
        <select
          required
          className={fieldClass}
          value={currency}
          onChange={(e) => setCurrency(e.target.value as FiatCurrency)}
        >
          <option value="">Not configured — select a currency</option>
          {config.currencies.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
      </label>
    </SettingsForm>
  );
}
function LegacyProvider({
  config,
  editable,
}: {
  config: PaymentWalletConfig;
  editable: boolean;
}) {
  const [currency, setCurrency] = useState<FiatCurrency>("NGN");
  const [provider, setProvider] = useState(
    config.activeProviderByCurrency.NGN || "none",
  );
  return (
    <SettingsForm
      title="Legacy payout provider override"
      editable={editable}
      save={() => api.saveProvider(currency, provider)}
    >
      <p className="text-sm text-gray-500">
        Applies only to routes using existing payout rules. Selecting No
        override restores the legacy range/default selection; it does not
        disable payouts.
      </p>
      <label className="block">
        Currency
        <select
          className={fieldClass}
          value={currency}
          onChange={(e) => {
            const c = e.target.value as FiatCurrency;
            setCurrency(c);
            setProvider(config.activeProviderByCurrency[c] || "none");
          }}
        >
          {config.currencies.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
      </label>
      <label className="block">
        Override
        <select
          className={fieldClass}
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
        >
          <option value="none">No override</option>
          {config.providers
            .filter((p) => p.key !== "none")
            .map((p) => (
              <option key={p.key} value={p.key}>
                {p.name || p.title || p.key}
              </option>
            ))}
        </select>
      </label>
    </SettingsForm>
  );
}
function RefreshSettings() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{
    message: string;
    warnings: string[];
  } | null>(null);
  const client = useQueryClient();
  return (
    <Card title="Reload system settings">
      <div className="p-5 space-y-3">
        <p className="text-sm text-gray-500">
          Reload saved database settings on the responding server process.
        </p>
        <Button
          variant="secondary"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              setResult(await api.refresh());
              await client.invalidateQueries("payment-operations");
            } catch (error) {
              showDanger(errorMessage(error));
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Reloading…" : "Reload saved settings"}
        </Button>
        {result && (
          <div role="status">
            <p>{result.message}</p>
            {result.warnings.map((warning, i) => (
              <p className="text-sm text-orange-700" key={i}>
                {warning}
              </p>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
function Minimums({
  config,
  editable,
}: {
  config: PaymentWalletConfig;
  editable: boolean;
}) {
  const [ghs, setGhs] = useState(
    String(config.directChargeMinDepositByCurrency?.GHS ?? ""),
  );
  const [kes, setKes] = useState(
    String(config.directChargeMinDepositByCurrency?.KES ?? ""),
  );
  return (
    <SettingsForm
      title="Direct charge deposit minimums"
      editable={editable}
      save={() => api.saveMinimums({ GHS: Number(ghs), KES: Number(kes) })}
    >
      {(
        [
          ["GHS", ghs, setGhs],
          ["KES", kes, setKes],
        ] as const
      ).map(([c, v, set]) => (
        <label key={c} className="block">
          Minimum ({c})
          <input
            className={fieldClass}
            required
            type="number"
            min="0.01"
            step="any"
            value={v}
            onChange={(e) => set(e.target.value)}
          />
        </label>
      ))}
    </SettingsForm>
  );
}
function Release({
  config,
  editable,
}: {
  config: PaymentWalletConfig;
  editable: boolean;
}) {
  const [value, setValue] = useState(config.pendingBalanceRelease);
  return (
    <SettingsForm
      title="Pending balance release"
      editable={editable}
      save={() => api.saveRelease(value)}
    >
      <label className="flex gap-3">
        <input
          type="checkbox"
          checked={value.enabled}
          onChange={(e) => setValue({ ...value, enabled: e.target.checked })}
        />
        Automatic release enabled
      </label>
      <div className="grid gap-4 sm:grid-cols-2">
        <label>
          Period
          <input
            className={fieldClass}
            required
            type="number"
            min="1"
            step="1"
            value={Number.isNaN(value.periodValue) ? "" : value.periodValue}
            onChange={(e) =>
              setValue({ ...value, periodValue: e.target.valueAsNumber })
            }
          />
        </label>
        <label>
          Unit
          <select
            className={fieldClass}
            value={value.periodUnit}
            onChange={(e) =>
              setValue({
                ...value,
                periodUnit: e.target.value as typeof value.periodUnit,
              })
            }
          >
            {["hours", "days", "months"].map((v) => (
              <option key={v}>{v}</option>
            ))}
          </select>
        </label>
      </div>
      {currencies.map((c) => (
        <label key={c} className="block">
          Limit ({c})
          <input
            className={fieldClass}
            required
            type="number"
            min="0"
            step="any"
            value={
              Number.isNaN(value.limitsByCurrency[c])
                ? ""
                : value.limitsByCurrency[c]
            }
            onChange={(e) =>
              setValue({
                ...value,
                limitsByCurrency: {
                  ...value.limitsByCurrency,
                  [c]: e.target.valueAsNumber,
                },
              })
            }
          />
        </label>
      ))}
    </SettingsForm>
  );
}
function Thresholds({
  data,
  editable,
}: {
  data: BalanceThresholds;
  editable: boolean;
}) {
  const [values, setValues] = useState(data.thresholds);
  return (
    <SettingsForm
      title="Operational balance alerts"
      editable={editable}
      save={() => api.saveThresholds(values)}
    >
      <p className="text-sm text-gray-500">
        Set a minimum balance per provider and currency. Leave a field blank to
        remove its threshold.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead>
            <tr>
              <th>Provider</th>
              {data.currencies.map((c) => (
                <th className="px-2" key={c}>
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.providers
              .filter((p) => p.key !== "none")
              .map((p) => (
                <tr key={p.key}>
                  <th className="pr-4 font-medium">
                    {p.name || p.title || p.key}
                  </th>
                  {data.currencies.map((c) => (
                    <td className="p-2" key={c}>
                      <input
                        aria-label={`${p.key} ${c} threshold`}
                        className={`${fieldClass} min-w-[100px]`}
                        type="number"
                        min="0"
                        step="any"
                        value={values[p.key]?.[c] ?? ""}
                        onChange={(e) => {
                          const next = { ...values[p.key] };
                          if (e.target.value === "") delete next[c];
                          else next[c] = e.target.valueAsNumber;
                          setValues({ ...values, [p.key]: next });
                        }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </SettingsForm>
  );
}
export default function PaymentOperations() {
  const { can, session } = useAdminAccess();
  const globalScope =
    session?.countryScope === "all" && currentAdminCountryScope() === "all";
  const config = useQuery(["payment-operations", "config"], api.config, {
    enabled: can("transactions.walletConfig.view"),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const thresholds = useQuery(
    ["payment-operations", "thresholds"],
    api.thresholds,
    {
      enabled: can("transactions.operationalBalanceThresholds.view"),
      retry: false,
      refetchOnWindowFocus: false,
    },
  );
  return (
    <Container>
      <AdminSidebar />
      <Main className="pb-10 mx-5 mt-5 text-left">
        <h1 className="text-2xl font-semibold">Payment operations</h1>
        <p className="my-4 text-sm text-gray-600">
          These settings apply across markets.
          {!globalScope &&
            " A global account with All markets selected is required to make changes."}
        </p>
        <div className="space-y-6">
          {can("transactions.walletConfig.view") &&
            (config.isError ? (
              <ErrorState error={config.error} retry={config.refetch} />
            ) : config.isLoading ? (
              <LoadingRows />
            ) : (
              config.data && (
                <div
                  key={`config-${config.dataUpdatedAt}`}
                  className="grid gap-6 lg:grid-cols-2"
                >
                  <Wallets
                    config={config.data}
                    editable={
                      globalScope && can("transactions.paymentMerchant.edit")
                    }
                  />
                  <LegacyProvider
                    config={config.data}
                    editable={
                      globalScope && can("transactions.paymentMerchant.edit")
                    }
                  />
                  <Minimums
                    config={config.data}
                    editable={
                      globalScope && can("transactions.walletConfig.edit")
                    }
                  />
                  <Release
                    config={config.data}
                    editable={
                      globalScope && can("transactions.walletConfig.edit")
                    }
                  />
                </div>
              )
            ))}
          {can("transactions.operationalBalanceThresholds.view") &&
            (thresholds.isError ? (
              <ErrorState error={thresholds.error} retry={thresholds.refetch} />
            ) : thresholds.isLoading ? (
              <LoadingRows />
            ) : (
              thresholds.data && (
                <Thresholds
                  key={`thresholds-${thresholds.dataUpdatedAt}`}
                  data={thresholds.data}
                  editable={
                    globalScope &&
                    can("transactions.operationalBalanceThresholds.edit")
                  }
                />
              )
            ))}
          {globalScope && can("transactions.systemSettings.edit") && (
            <RefreshSettings />
          )}
        </div>
      </Main>
    </Container>
  );
}
